/* eslint-disable class-methods-use-this */
/* https://github.com/kensnyder/quill-image-resize-module */
/* The MIT License
Copyright © 2018

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the “Software”), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED “AS IS”, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE. */

import Quill from 'quill';
import ReactDOMServer from 'react-dom/server';

import {
  IconAlignLeft,
  IconAlignCenter,
  IconAlignRight,
} from '@hivebrite/design-system/Icon';

import { BaseModule } from './BaseModule';

const Parchment = Quill.imports.parchment;
const FloatStyle = new Parchment.Attributor.Style('float', 'float');
const MarginStyle = new Parchment.Attributor.Style('margin', 'margin');
const DisplayStyle = new Parchment.Attributor.Style('display', 'display');

export class Toolbar extends BaseModule {
  onCreate = () => {
    // Setup Toolbar
    this.toolbar = document.createElement('div');
    Object.assign(this.toolbar.style, this.options.toolbarStyles);
    this.overlay.appendChild(this.toolbar);

    // Setup Buttons
    this._defineAlignments();
    this._addToolbarButtons();
  };

  // The toolbar and its children will be destroyed when the overlay is removed
  onDestroy = () => {};

  // Nothing to update on drag because we are are positioned relative to the overlay
  onUpdate = () => {};

  _defineAlignments = () => {
    this.alignments = [
      {
        icon: ReactDOMServer.renderToString(<IconAlignLeft />),
        apply: (target) => {
          DisplayStyle.add(target, 'inline');
          FloatStyle.add(target, 'left');
          MarginStyle.add(target, '0 1em 1em 0');
        },
        isApplied: (target) => FloatStyle.value(target) === 'left',
      },
      {
        icon: ReactDOMServer.renderToString(<IconAlignCenter />),
        apply: (target) => {
          DisplayStyle.add(target, 'block');
          FloatStyle.remove(target);
          MarginStyle.add(target, 'auto');
        },
        isApplied: (target) => MarginStyle.value(target) === 'auto',
      },
      {
        icon: ReactDOMServer.renderToString(<IconAlignRight />),
        apply: (target) => {
          DisplayStyle.add(target, 'inline');
          FloatStyle.add(target, 'right');
          MarginStyle.add(target, '0 0 1em 1em');
        },
        isApplied: (target) => FloatStyle.value(target) === 'right',
      },
    ];
  };

  _addToolbarButtons = () => {
    const buttons = [];

    this.alignments.forEach((alignment, idx) => {
      const button = document.createElement('span');

      buttons.push(button);
      button.innerHTML = alignment.icon;
      button.addEventListener('click', () => {
        // deselect all buttons
        buttons.forEach((button) => (button.style.filter = ''));
        // inline-image can be wrapped inside an <a> tag
        const target =
          this.img.parentNode.tagName === 'A' ? this.img.parentNode : this.img;

        if (alignment.isApplied(target)) {
          // If applied, unapply
          FloatStyle.remove(target);
          MarginStyle.remove(target);
          DisplayStyle.remove(target);
        } else {
          // otherwise, select button and apply
          this._selectButton(button);
          alignment.apply(target);
        }
        // image may change position; redraw drag handles
        this.requestUpdate();
      });
      Object.assign(button.style, this.options.toolbarButtonStyles);
      if (idx > 0) {
        button.style.borderLeftWidth = '0';
      }
      // Object.assign(
      //   button.children[0].style,
      //   this.options.toolbarButtonSvgStyles,
      // );
      // inline-image can be wrapped inside an <a> tag
      const target =
        this.img.parentNode.tagName === 'A' ? this.img.parentNode : this.img;

      if (alignment.isApplied(target)) {
        // select button if previously applied
        this._selectButton(button);
      }
      this.toolbar.appendChild(button);
    });
  };

  _selectButton = (button) => {
    button.style.filter = 'invert(20%)';
  };
}
